import { useState } from 'react';
import { Download, FileSpreadsheet, Loader2 } from 'lucide-react';
import { cutoverApi } from '../../services/cutoverApi';

export default function ClinicMatrixExportTab() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastExport, setLastExport] = useState<string | null>(null);

  const handleExport = async () => {
    setLoading(true);
    setError(null);
    try {
      const blob = await cutoverApi.exportClinicMatrix();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ClinicMatrix_Export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setLastExport(new Date().toLocaleString());
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Export failed';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tab-content-section">
      <div className="section-header">
        <FileSpreadsheet size={20} />
        <h2>Clinic Matrix Export</h2>
      </div>

      <div className="section-body" style={{ maxWidth: 640 }}>
        <p style={{ marginBottom: 16, color: 'var(--text-secondary)' }}>
          Export the current database state as an Excel workbook matching the Clinic Matrix
          import format. The workbook contains two sheets:
        </p>
        <ul style={{ marginBottom: 24, paddingLeft: 20, color: 'var(--text-secondary)' }}>
          <li><strong>Clinic Matrix</strong> — One row per customer-location-clinic relationship (Client Name, Clinic Code, State, City, Clinic Name, capabilities)</li>
          <li><strong>Clinic Codes</strong> — One row per clinic with all capability flags (eScreen, Formfox, Fit Test, Mask Types, PAT, X-Ray, etc.)</li>
        </ul>
        <p style={{ marginBottom: 24, color: 'var(--text-secondary)', fontSize: '0.9em' }}>
          The exported file can be re-imported through the Clinic Matrix Import tab for round-trip
          comparison, or opened in Excel alongside the original spreadsheet to identify differences.
        </p>

        <button
          type="button"
          className="btn btn-primary"
          onClick={handleExport}
          disabled={loading}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          {loading ? (
            <>
              <Loader2 size={16} className="spinner" />
              Generating...
            </>
          ) : (
            <>
              <Download size={16} />
              Download Clinic Matrix
            </>
          )}
        </button>

        {error && (
          <div className="alert alert-error" style={{ marginTop: 16 }}>
            {error}
          </div>
        )}

        {lastExport && !error && (
          <p style={{ marginTop: 12, fontSize: '0.85em', color: 'var(--text-tertiary)' }}>
            Last exported: {lastExport}
          </p>
        )}
      </div>
    </div>
  );
}
